import { skipToken } from '@reduxjs/toolkit/query';
import Big from 'big.js';
import { RecordLink } from 'components/record-chip/RecordLink';
import { formatHoursToHm } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { resolveHourlyPriceOn } from 'pages/pricelists/matterRateResolution';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useFindMatterRatesQuery } from 'store/api/matters';
import { NamedEntityMinDto } from 'types/other';
import { t } from 'utils/getTranslations';

type Props = {
    authors: NamedEntityMinDto[];
    billedTimeInHours?: string | null;
    multiplierRate?: string;
    isNonBillable?: boolean;
    matterId?: number;
    timecardDate?: string | null;
    currency?: string | null;
};

export const toNumber = (value?: string | null): number => Number((value ?? '').toString().replace(/,/g, '.'));

// Null when the author has no rate on the matter, so the row can say "-" instead of
// implying a free hour. A non-billable entry costs nothing even when its billed hours are preserved.
export const calculateCost = (
    hourlyPrice: string | null | undefined,
    billedHours: number,
    multiplier: number,
    isNonBillable?: boolean
): string | null => {
    const rate = toNumber(hourlyPrice);
    if (hourlyPrice === null || hourlyPrice === undefined || !Number.isFinite(rate) || !Number.isFinite(billedHours)) {
        return null;
    }
    if (isNonBillable) {
        return '0.00';
    }
    return new Big(rate)
        .times(billedHours)
        .times(Number.isFinite(multiplier) ? multiplier : 1)
        .toFixed(2);
};

// One cost line per timecard author: each author is billed at their own matter rate, so a multi-author
// entry costs the sum of the lines, not the current user's rate times the author count. The rate is the
// one the saved timecard gets: from the case's list covering the timecard's date, not the list in force today.
const TimecardCostBreakdown = ({
    authors,
    billedTimeInHours,
    multiplierRate,
    isNonBillable,
    matterId,
    timecardDate,
    currency,
}: Props): JSX.Element | null => {
    const authorIds = authors.map((author) => author.id);
    // currentData, not data: while the case changes, the previous case's lists must not price the authors.
    const { currentData: matterPricelists } = useFindMatterRatesQuery(matterId == null ? skipToken : String(matterId));
    // A single author reuses the form's own employee query args verbatim, so both share one cache entry.
    const { data: employees } = useGetEmployeesListWithFiltersQuery(
        authorIds.length
            ? {
                  pageSize: authorIds.length,
                  page: 0,
                  filters: authorIds.length === 1 ? `id==${authorIds[0]}` : `id=in=(${authorIds.join(',')})`,
              }
            : skipToken
    );

    const billedHours = toNumber(billedTimeInHours);
    if (!authorIds.length || !currency || !Number.isFinite(billedHours)) {
        return null;
    }

    const rows = authors.map((author) => {
        const employmentHistory = employees?.content.find((employee) => employee.id === author.id)?.employmentHistory;
        const hourlyPrice = timecardDate
            ? resolveHourlyPriceOn(matterPricelists, author.id, employmentHistory, timecardDate)
            : undefined;

        return {
            author,
            hourlyPrice,
            cost: calculateCost(hourlyPrice, billedHours, toNumber(multiplierRate || '1.0'), isNonBillable),
        };
    });

    return (
        <div className="border-grey-300 border-t border-b py-2">
            {/* table-fixed: column widths come from the header, not the cell text, so the columns hold
                still while the time cell grows from "1h" to "1h 30min" as billed time is typed. */}
            <table className="w-full table-fixed text-left tabular-nums">
                <thead className="text-grey-500 text-sm">
                    <tr>
                        <th className="w-2/5 px-2 py-1 font-medium">{t('timecards.form.tc-for')}</th>
                        <th className="w-1/5 px-2 py-1 font-medium">{t('timecards.form.time')}</th>
                        <th className="w-1/5 px-2 py-1 font-medium">
                            {t('timecards.form.hourly-rate-with-currency', { currency })}
                        </th>
                        <th className="w-1/5 px-2 py-1 font-medium">
                            {t('timecards.form.cost-with-currency', { currency })}
                        </th>
                    </tr>
                </thead>
                <tbody className="text-[13px]">
                    {rows.map(({ author, hourlyPrice, cost }) => (
                        <tr key={author.id} className="even:bg-grey-100">
                            <td className="truncate px-2 py-1">
                                {/* New tab: the breakdown sits inside the timecard form, so navigating
                                    in place would throw away whatever is half-typed above it. */}
                                {/* The app's link style — black at rest, blue and underlined on hover —
                                    spelled out, since no table's link rule reaches this plain table. */}
                                <RecordLink
                                    anchor
                                    to={`/employee/${author.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-fg hover:text-link no-underline hover:underline"
                                    label={author.name}
                                />
                            </td>
                            <td className="px-2 py-1">{formatHoursToHm(billedHours) || '-'}</td>
                            <td className="px-2 py-1">
                                {cost === null ? (
                                    '-'
                                ) : (
                                    <>
                                        <MoneyDisplay amount={toNumber(hourlyPrice)} />
                                        {t('timecards.form.per-hour-suffix')}
                                    </>
                                )}
                            </td>
                            <td className="px-2 py-1">
                                {cost === null ? '-' : <MoneyDisplay amount={cost} currency={currency} />}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TimecardCostBreakdown;
